import { ItemCategory } from "./types";
import { categoryLabels } from "./categoryLabels";

// Define the subcategory type
export type SubCategory = {
  id: string;
  name: string;
  parentCategory: ItemCategory;
};

// Define the category structure type
export type CategoryStructure = {
  id: ItemCategory;
  name: string;
  subcategories: SubCategory[];
};

// Create the subcategories mapping
export const subcategories: SubCategory[] = [
  // Baby & Kids
  { id: "baby-cot", name: "Baby Cot", parentCategory: "baby" },
  { id: "baby-high-chair", name: "Baby High Chair", parentCategory: "baby" },
  { id: "baby-stroller", name: "Baby Stroller", parentCategory: "baby" },

  // Beds & Bedding
  { id: "bed-frame", name: "Bed Frame", parentCategory: "bedding" },
  {
    id: "bed-frame-mattress",
    name: "Bed Frame & Mattress",
    parentCategory: "bedding",
  },
  { id: "mattress", name: "Mattress", parentCategory: "bedding" },

  // Home Decor
  { id: "curtains-blind", name: "Curtains & Blind", parentCategory: "decor" },
  { id: "mirror", name: "Mirror", parentCategory: "decor" },
  { id: "rug-carpet", name: "Rug & Carpet", parentCategory: "decor" },
  { id: "wall-art-frame", name: "Wall Art & Frame", parentCategory: "decor" },

  // Electronics & Appliances
  {
    id: "air-conditioner",
    name: "Air Conditioner",
    parentCategory: "electronics",
  },
  {
    id: "audio-video-player",
    name: "Audio & Video Player",
    parentCategory: "electronics",
  },
  { id: "camera", name: "Camera", parentCategory: "electronics" },
  // Fans: canonical `fan` + legacy four-way split (aliases until picker hide)
  { id: "fan", name: "Fan", parentCategory: "electronics" },
  { id: "ceiling-fan", name: "Ceiling Fan", parentCategory: "electronics" },
  { id: "chandelier", name: "Chandelier", parentCategory: "electronics" },
  {
    id: "home-theatre-system",
    name: "Home Theatre System",
    parentCategory: "electronics",
  },
  {
    id: "inverter-air-conditioner",
    name: "Inverter Air Conditioner",
    parentCategory: "electronics",
  },
  { id: "light", name: "Light", parentCategory: "electronics" },
  {
    id: "midi-player-piano",
    name: "Midi Player Piano",
    parentCategory: "electronics",
  },
  { id: "non-smart-tv", name: "Non-Smart TV", parentCategory: "electronics" },
  { id: "pressing-iron", name: "Pressing Iron", parentCategory: "electronics" },
  {
    id: "rechargeable-fans",
    name: "Rechargeable Fans",
    parentCategory: "electronics",
  },
  { id: "router-modem", name: "Router & Modem", parentCategory: "electronics" },
  { id: "smart-tv", name: "Smart TV", parentCategory: "electronics" },
  { id: "soundbar", name: "Soundbar", parentCategory: "electronics" },
  { id: "speaker", name: "Speaker", parentCategory: "electronics" },
  /**
   * Deprecated sell picker id (Wave 2 audio split). Prefer `soundbar` / `speaker`.
   */
  {
    id: "soundbar-speaker",
    name: "Soundbar & Speaker",
    parentCategory: "electronics",
  },
  { id: "sound-system", name: "Sound System", parentCategory: "electronics" },
  { id: "standing-fan", name: "Standing Fan", parentCategory: "electronics" },
  { id: "stabilizer", name: "Stabilizer", parentCategory: "electronics" },
  {
    id: "vacuum-cleaner",
    name: "Vacuum Cleaner",
    parentCategory: "electronics",
  },
  { id: "wall-fan", name: "Wall Fan", parentCategory: "electronics" },
  {
    id: "washing-machine",
    name: "Washing Machine",
    parentCategory: "electronics",
  },
  {
    id: "inverter-washing-machine",
    name: "Inverter Washing Machine",
    parentCategory: "electronics",
  },
  {
    id: "water-dispenser",
    name: "Water Dispenser",
    parentCategory: "electronics",
  },

  // Personal Computers (laptops and desktops)
  {
    id: "desktop-pc",
    name: "Desktop PC",
    parentCategory: "personal_computers",
  },
  { id: "monitor", name: "Monitor", parentCategory: "personal_computers" },
  { id: "win-pc", name: "Windows PC", parentCategory: "personal_computers" },
  { id: "mac", name: "Mac", parentCategory: "personal_computers" },

  // Phones & Tabs
  { id: "iphone", name: "iPhone", parentCategory: "phones_tabs" },
  { id: "ipad", name: "iPad", parentCategory: "phones_tabs" },
  { id: "android-phone", name: "Android Phone", parentCategory: "phones_tabs" },
  { id: "android-tab", name: "Android Tablet", parentCategory: "phones_tabs" },

  // Digital Wears & Accessories
  { id: "iwatch", name: "iWatch", parentCategory: "digital_wears" },
  { id: "smart-watch", name: "Smartwatch", parentCategory: "digital_wears" },
  { id: "vr-headset", name: "VR Headset", parentCategory: "digital_wears" },

  // Gaming
  { id: "play-station", name: "Play Station", parentCategory: "gaming" },
  { id: "snooker-board", name: "Snooker", parentCategory: "gaming" },
  {
    id: "other-game-consoles",
    name: "Other Game Consoles",
    parentCategory: "gaming",
  },

  // Power Solutions
  { id: "generator", name: "Generator", parentCategory: "power_solutions" },
  { id: "inverter", name: "Inverter", parentCategory: "power_solutions" },
  {
    id: "inverter-battery",
    name: "Inverter Battery",
    parentCategory: "power_solutions",
  },

  // Fashion Business Tools
  {
    id: "sewing-machine",
    name: "Sewing Machine",
    parentCategory: "fashion",
  },
  /**
   * Deprecated sell picker id (sewing-machine-taxonomy). Prefer `sewing-machine`
   * + `sewing_machine_type`. Kept for reads / edit of legacy rows until retag.
   */
  {
    id: "electric-sewing-machine",
    name: "Electric Sewing Machine",
    parentCategory: "fashion",
  },
  {
    id: "industrial-sewing-machine",
    name: "Industrial Sewing Machine",
    parentCategory: "fashion",
  },
  { id: "mannequin", name: "Mannequin", parentCategory: "fashion" },
  {
    id: "manual-sewing-machine",
    name: "Manual Sewing Machine",
    parentCategory: "fashion",
  },

  // Sports & Fitness
  { id: "bicycle", name: "Bicycle", parentCategory: "fitness" },
  { id: "gym-bike", name: "Gym Bike", parentCategory: "fitness" },
  { id: "treadmill", name: "Treadmill", parentCategory: "fitness" },

  // Furniture
  { id: "center-table", name: "Center Table", parentCategory: "furniture" },
  {
    id: "chair",
    name: "Chair",
    parentCategory: "furniture",
  },
  /**
   * Deprecated sell picker id (L-shaped taxonomy B1). Prefer `sofa-couche` + `sofa_shape=l_shaped`.
   * Kept for reads / edit of legacy rows until Phase 3 migrate.
   */
  {
    id: "l-shaped-sofa",
    name: "L-shaped Sofa",
    parentCategory: "furniture",
  },
  {
    id: "office-table-chairs",
    name: "Office Desk & Chairs",
    parentCategory: "furniture",
  },
  { id: "office-table", name: "Office Table", parentCategory: "furniture" },
  { id: "shelf", name: "Shelf", parentCategory: "furniture" },
  { id: "sofa-couche", name: "Sofa", parentCategory: "furniture" },
  { id: "table", name: "Table", parentCategory: "furniture" },
  { id: "table-chair", name: "Table & Chair", parentCategory: "furniture" },
  { id: "tv-console", name: "TV Console", parentCategory: "furniture" },
  { id: "wardrobe", name: "Wardrobe", parentCategory: "furniture" },
  { id: "dresser", name: "Dresser", parentCategory: "furniture" },
  /**
   * Deprecated sell picker id (Wave 2 furniture). Prefer `wardrobe` / `dresser`.
   * Kept for reads, reference backfill, and alias resolution until rows migrate.
   */
  {
    id: "wardrobe-dresser",
    name: "Wardrobe & Dresser",
    parentCategory: "furniture",
  },

  // Kitchen & Dining
  { id: "air-fryer", name: "Air Fryer", parentCategory: "kitchen" },
  {
    id: "blender-food-processor",
    name: "Blender & Food Processor",
    parentCategory: "kitchen",
  },
  {
    id: "cookware-utensil",
    name: "Cookware & Utensil",
    parentCategory: "kitchen",
  },
  { id: "food-cooler", name: "Food Cooler", parentCategory: "kitchen" },
  // Refrigerators: canonical `fridge` + legacy shape/inverter ids (aliases); chest freezers separate
  { id: "chest-freezers", name: "Chest Freezers", parentCategory: "kitchen" },
  {
    id: "fridge",
    name: "Fridge",
    parentCategory: "kitchen",
  },
  /**
   * Deprecated sell picker ids (Wave 2 kitchen fridge). Prefer `fridge` + door/inverter/smart attrs.
   */
  { id: "inverter-fridge", name: "Inverter Fridge", parentCategory: "kitchen" },
  { id: "mini-fridge", name: "Mini Fridge", parentCategory: "kitchen" },
  { id: "standing-fridge", name: "Standing Fridge", parentCategory: "kitchen" },
  {
    id: "side-by-side-fridge",
    name: "Side-by-Side Fridge",
    parentCategory: "kitchen",
  },
  { id: "dish-washer", name: "Dish Washer", parentCategory: "kitchen" },
  // Gas Cookers: Standing Gas Cooker, Standing Gas Cooker & Cylinder, Table-top Gas Cooker, Table-top Gas Cooker & Cylinder
  { id: "gas-cylinder", name: "Gas Cylinder", parentCategory: "kitchen" },
  // Gas cookers: canonical `gas-cooker` + legacy four-way split (aliases until Phase 3 backfill)
  {
    id: "gas-cooker",
    name: "Gas Cooker",
    parentCategory: "kitchen",
  },
  /**
   * Deprecated sell picker ids (Wave 2 gas cooker). Prefer `gas-cooker` + mount/cylinder attrs.
   * Kept for reads, reference backfill, and alias resolution until rows migrate.
   */
  {
    id: "standing-gas-cooker",
    name: "Standing Gas Cooker",
    parentCategory: "kitchen",
  },
  {
    id: "standing-gas-cooker-cylinder",
    name: "Standing Gas Cooker & Cylinder",
    parentCategory: "kitchen",
  },
  {
    id: "table-top-gas-cooker",
    name: "Table-top Gas Cooker",
    parentCategory: "kitchen",
  },
  {
    id: "table-top-gas-cooker-cylinder",
    name: "Table-top Gas Cooker & Cylinder",
    parentCategory: "kitchen",
  },
  // Microwaves: canonical `microwave` + legacy four-way split (aliases until Phase 4)
  {
    id: "microwave",
    name: "Microwave",
    parentCategory: "kitchen",
  },
  {
    id: "inverter-microwave",
    name: "Inverter Microwave",
    parentCategory: "kitchen",
  },
  {
    id: "non-inverter-microwave",
    name: "Non-Inverter Microwave",
    parentCategory: "kitchen",
  },
  {
    id: "non-smart-microwave",
    name: "Non-Smart Microwave",
    parentCategory: "kitchen",
  },
  { id: "smart-microwave", name: "Smart Microwave", parentCategory: "kitchen" },
  {
    id: "dining-table-chair",
    name: "Dining Table & Chair",
    parentCategory: "furniture",
  },
  {
    id: "kitchen-cabinet",
    name: "Kitchen Cabinet",
    parentCategory: "furniture",
  },
  {
    id: "hanging-kitchen-cabinet",
    name: "Hanging Kitchen Cabinet",
    parentCategory: "furniture",
  },

  // Outdoor & Garden
  { id: "bbq-grill", name: "Barbeque Grill", parentCategory: "outdoor" },
  { id: "garden-tool", name: "Garden Tool", parentCategory: "outdoor" },
  { id: "water-tank", name: "Water Tank", parentCategory: "outdoor" },
];

/** Legacy combined wardrobe+dresser id — reads/backfill only after wardrobe + dresser ship. */
export const LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID =
  "wardrobe-dresser" as const;

/**
 * Deprecated furniture subcategory → canonical sell replacements (Wave 2).
 * Sell pickers should offer the replacements, not the legacy id.
 */
export const DEPRECATED_FURNITURE_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  [LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID]: ["wardrobe", "dresser"],
  /** L-shaped taxonomy B1 — shape is an attribute on Sofa, not a sibling subcategory. */
  "l-shaped-sofa": ["sofa-couche"],
  "l-shaped-chair": ["sofa-couche"],
};

export function isDeprecatedFurnitureSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_FURNITURE_SUBCATEGORY_REPLACEMENTS
  );
}

/**
 * Deprecated appliance inverter subcategory → canonical sell id (Wave 2).
 * Hide legacy ids from sell picker after family migration (Phases 2–4).
 */
export const DEPRECATED_APPLIANCE_INVERTER_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "inverter-air-conditioner": ["air-conditioner"],
  "inverter-washing-machine": ["washing-machine"],
  "inverter-microwave": ["microwave"],
  "non-inverter-microwave": ["microwave"],
  "smart-microwave": ["microwave"],
  "non-smart-microwave": ["microwave"],
};

/**
 * Legacy appliance ids currently hidden from sell/market pickers
 * (AC + washer + microwave families — Phases 2–4).
 */
export const SELL_PICKER_HIDDEN_APPLIANCE_INVERTER_IDS: ReadonlySet<string> =
  new Set([
    "inverter-air-conditioner",
    "inverter-washing-machine",
    "inverter-microwave",
    "non-inverter-microwave",
    "smart-microwave",
    "non-smart-microwave",
  ]);

export function isDeprecatedApplianceInverterSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_APPLIANCE_INVERTER_SUBCATEGORY_REPLACEMENTS
  );
}

/** True when the id must not appear in sell/market pickers (phased hide). */
export function isHiddenFromSellPickerApplianceSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_APPLIANCE_INVERTER_IDS.has(subcategoryId)
  );
}

/**
 * Deprecated fan subcategory → canonical sell id (Wave 2 fan unified).
 * Hide legacy ids from sell picker after Phase 2.
 */
export const DEPRECATED_FAN_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "standing-fan": ["fan"],
  "wall-fan": ["fan"],
  "ceiling-fan": ["fan"],
  "rechargeable-fans": ["fan"],
};

/** Legacy fan ids hidden from sell/market pickers (Phase 2). */
export const SELL_PICKER_HIDDEN_FAN_IDS: ReadonlySet<string> = new Set([
  "standing-fan",
  "wall-fan",
  "ceiling-fan",
  "rechargeable-fans",
]);

export function isDeprecatedFanSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_FAN_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerFanSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_FAN_IDS.has(subcategoryId)
  );
}

/**
 * Deprecated gas cooker subcategory → canonical sell id (Wave 2 gas cooker unified).
 * Hide legacy ids from sell picker after Phase 2.
 */
export const DEPRECATED_GAS_COOKER_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "standing-gas-cooker": ["gas-cooker"],
  "table-top-gas-cooker": ["gas-cooker"],
  "standing-gas-cooker-cylinder": ["gas-cooker"],
  "table-top-gas-cooker-cylinder": ["gas-cooker"],
};

/** Legacy gas cooker ids hidden from sell/market pickers (Phase 2). `gas-cylinder` stays visible. */
export const SELL_PICKER_HIDDEN_GAS_COOKER_IDS: ReadonlySet<string> = new Set([
  "standing-gas-cooker",
  "table-top-gas-cooker",
  "standing-gas-cooker-cylinder",
  "table-top-gas-cooker-cylinder",
]);

export function isDeprecatedGasCookerSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_GAS_COOKER_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerGasCookerSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_GAS_COOKER_IDS.has(subcategoryId)
  );
}

/** Legacy inverter-fridge hidden from sell picker (Wave 2 kitchen fridge Phase 2). */
export const DEPRECATED_FRIDGE_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "standing-fridge": ["fridge"],
  "mini-fridge": ["fridge"],
  "inverter-fridge": ["fridge"],
  "side-by-side-fridge": ["fridge"],
};

export const SELL_PICKER_HIDDEN_FRIDGE_IDS: ReadonlySet<string> = new Set([
  "standing-fridge",
  "mini-fridge",
  "inverter-fridge",
  "side-by-side-fridge",
]);

export function isDeprecatedFridgeSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_FRIDGE_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerFridgeSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_FRIDGE_IDS.has(subcategoryId)
  );
}

/**
 * Deprecated kitchen cabinet subcategory → canonical sell id (Wave 2).
 * Hide legacy hanging id from sell picker after Phase 2.
 */
export const DEPRECATED_KITCHEN_CABINET_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "hanging-kitchen-cabinet": ["kitchen-cabinet"],
};

export const SELL_PICKER_HIDDEN_KITCHEN_CABINET_IDS: ReadonlySet<string> =
  new Set(["hanging-kitchen-cabinet"]);

export function isDeprecatedKitchenCabinetSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_KITCHEN_CABINET_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerKitchenCabinetSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_KITCHEN_CABINET_IDS.has(subcategoryId)
  );
}

/**
 * Deprecated Other Game Consoles → canonical Snooker (`snooker-board`).
 * Hide legacy id from sell picker after Phase 1.
 */
export const DEPRECATED_SNOOKER_BOARD_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "other-game-consoles": ["snooker-board"],
};

export const SELL_PICKER_HIDDEN_SNOOKER_BOARD_IDS: ReadonlySet<string> =
  new Set(["other-game-consoles"]);

export function isDeprecatedSnookerBoardSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_SNOOKER_BOARD_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerSnookerBoardSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_SNOOKER_BOARD_IDS.has(subcategoryId)
  );
}

/**
 * Deprecated sewing machine subcategory → canonical sell id.
 * Hide legacy Manual/Electric/Industrial peers from sell picker.
 */
export const DEPRECATED_SEWING_MACHINE_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  "manual-sewing-machine": ["sewing-machine"],
  "electric-sewing-machine": ["sewing-machine"],
  "industrial-sewing-machine": ["sewing-machine"],
};

export const SELL_PICKER_HIDDEN_SEWING_MACHINE_IDS: ReadonlySet<string> =
  new Set([
    "manual-sewing-machine",
    "electric-sewing-machine",
    "industrial-sewing-machine",
  ]);

export function isDeprecatedSewingMachineSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_SEWING_MACHINE_SUBCATEGORY_REPLACEMENTS
  );
}

export function isHiddenFromSellPickerSewingMachineSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    SELL_PICKER_HIDDEN_SEWING_MACHINE_IDS.has(subcategoryId)
  );
}

/** Legacy combined soundbar+speaker id — reads/backfill only after soundbar + speaker ship. */
export const LEGACY_SOUNDBAR_SPEAKER_SUBCATEGORY_ID =
  "soundbar-speaker" as const;

/**
 * Deprecated electronics audio subcategory → canonical sell replacements (Wave 2).
 */
export const DEPRECATED_AUDIO_SUBCATEGORY_REPLACEMENTS: Readonly<
  Record<string, readonly string[]>
> = {
  [LEGACY_SOUNDBAR_SPEAKER_SUBCATEGORY_ID]: ["soundbar", "speaker"],
};

export function isDeprecatedAudioSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return (
    typeof subcategoryId === "string" &&
    subcategoryId in DEPRECATED_AUDIO_SUBCATEGORY_REPLACEMENTS
  );
}

// Helper function to get subcategories by parent category
export const getSubcategoriesByParent = (
  parentCategory: ItemCategory,
): SubCategory[] => {
  return subcategories.filter((sub) => sub.parentCategory === parentCategory);
};

// Create the full category structure
export const categoryStructure: CategoryStructure[] = Object.entries(
  categoryLabels,
).map(([id, name]) => ({
  id: id as ItemCategory,
  name,
  subcategories: getSubcategoriesByParent(id as ItemCategory),
}));

// Helper function to search through categories and subcategories
export const searchCategories = (
  searchTerm: string,
): (CategoryStructure | SubCategory)[] => {
  const normalizedSearch = searchTerm.toLowerCase();

  const results: (CategoryStructure | SubCategory)[] = [];

  // Search in main categories
  categoryStructure.forEach((category) => {
    if (category.name.toLowerCase().includes(normalizedSearch)) {
      results.push(category);
    }

    // Search in subcategories
    category.subcategories.forEach((subcategory) => {
      if (subcategory.name.toLowerCase().includes(normalizedSearch)) {
        results.push(subcategory);
      }
    });
  });

  return results;
};
